<nav id="main-nav" class="main-nav">
	<ul class="main-nav-ul">
    {#each $navPages as navPage, i (navPage.href)}
      <li
        class="main-nav-li{dragIndex === i ? ' is-dragging' : ''}{overIndex === i && dragIndex !== i ? ' drag-over' : ''}{touchHeld === i ? ' touch-held' : ''}"
        draggable="true"
        data-index="{i}"
        on:dragstart="onDragStart(event, i)"
        on:dragend="onDragEnd()"
        on:dragover="onDragOver(event, i)"
        on:drop="onDrop(i)"
        on:touchstart="onTouchStart(event, i)"
        on:touchmove="onTouchMove(event)"
        on:touchend="onTouchEnd(event)"
        on:touchcancel="onTouchCancel()"
        on:keydown="onKeydown(event, i)"
      >
        <NavItem
          {page}
          name={navPage.name}
          href={navPage.href}
          svg={navPage.svg}
          label={navPage.label}
        />
      </li>
    {/each}
	</ul>
  <span id="nav-reorder-hint" class="sr-only">{$messages["tabReorderHint"]}</span>
</nav>
{#if navShortcuts}
  <svelte:component this={navShortcuts}/>
{/if}

<style>
	.main-nav {
    box-sizing: border-box;
		border-bottom: var(--nav-border-bottom) solid var(--nav-border);
		background: var(--nav-bg);
		position: fixed;
		inset-inline: 0;
		top: var(--nav-top);
    bottom: var(--nav-bottom);
		z-index: 100;
    contain: content; /* see https://www.w3.org/TR/2018/CR-css-contain-1-20181108/#valdef-contain-content */
	}

  .main-nav-ul {
		margin: 0;
		padding: 0;
		list-style: none;
		display: flex;
		align-items: stretch;
	}

  .main-nav-li {
    display: flex;
    transition: opacity 0.15s, transform 0.15s;
    cursor: grab;
  }

  .main-nav-li.is-dragging {
    opacity: 0.35;
    cursor: grabbing;
  }

  .main-nav-li.drag-over {
    box-shadow: inset 2px 0 0 var(--anchor-text);
  }

  .main-nav-li.touch-held {
    opacity: 0.6;
    transform: scale(0.92);
  }

  @media (max-width: 991px) {
    .main-nav-li {
      flex: 1;
    }

    .main-nav-li.drag-over {
      background: var(--nav-bg);
      box-shadow: inset 2px 0 0 var(--anchor-text);
    }
  }
</style>
<script>
  import NavItem from './NavItem.html'
  import { importNavShortcuts } from '../_utils/asyncModules/importNavShortcuts.js'
  import { store } from '../_store/store.js'
  import { announceAriaLivePolite } from '../_utils/announceAriaLivePolite.js'
  import { formatIntl } from '../_utils/formatIntl.js'

  export default {
    async oncreate () {
      const navShortcuts = await importNavShortcuts()
      this.set({ navShortcuts })
    },
    ondestroy () {
      if (this._touchTimer) {
        clearTimeout(this._touchTimer)
        this._touchTimer = null
      }
    },
    store: () => store,
    components: {
      NavItem
    },
    data: () => ({
      navShortcuts: undefined,
      dragIndex: null,
      overIndex: null,
      touchHeld: null,
      _touchTimer: null,
      _touchDragActive: false,
      _touchDragIndex: null,
      _touchStartX: 0,
      _touchStartY: 0
    }),
    methods: {
      onDragStart (event, index) {
        this.set({ dragIndex: index, overIndex: null })
        event.dataTransfer.effectAllowed = 'move'
      },
      onDragEnd () {
        this.set({ dragIndex: null, overIndex: null })
      },
      onDragOver (event, index) {
        event.preventDefault()
        event.dataTransfer.dropEffect = 'move'
        const { dragIndex } = this.get()
        if (dragIndex !== null && dragIndex !== index) {
          this.set({ overIndex: index })
        }
      },
      onDrop (toIndex) {
        const { dragIndex } = this.get()
        if (dragIndex !== null && dragIndex !== toIndex) {
          this._reorderTabs(dragIndex, toIndex)
        }
        this.set({ dragIndex: null, overIndex: null })
      },

      onTouchStart (event, index) {
        const touch = event.touches[0]
        const timer = setTimeout(() => {
          if (navigator.vibrate) navigator.vibrate(40)
          this.set({ touchHeld: index, overIndex: index })
          this._touchDragActive = true
          this._touchDragIndex = index
        }, 500)
        this._touchTimer = timer
        this._touchDragActive = false
        this._touchDragIndex = null
        this._touchStartX = touch.clientX
        this._touchStartY = touch.clientY
      },
      onTouchMove (event) {
        const touch = event.touches[0]

        if (!this._touchDragActive) {
          const dx = Math.abs(touch.clientX - this._touchStartX)
          const dy = Math.abs(touch.clientY - this._touchStartY)
          if (dx > 8 || dy > 8) {
            clearTimeout(this._touchTimer)
            this._touchTimer = null
          }
          return
        }

        event.preventDefault()

        const elements = document.elementsFromPoint(touch.clientX, touch.clientY)
        const navLi = elements.find(el => el.classList.contains('main-nav-li'))
        if (navLi) {
          const idx = parseInt(navLi.dataset.index, 10)
          if (!isNaN(idx) && idx !== this._touchDragIndex) {
            this.set({ overIndex: idx })
          }
        }
      },
      onTouchEnd (event) {
        clearTimeout(this._touchTimer)
        this._touchTimer = null

        const { overIndex } = this.get()
        if (this._touchDragActive && this._touchDragIndex !== null && overIndex !== null && this._touchDragIndex !== overIndex) {
          event.preventDefault()
          this._reorderTabs(this._touchDragIndex, overIndex)
        }

        this._touchDragActive = false
        this._touchDragIndex = null
        this.set({ touchHeld: null, overIndex: null })
      },
      onTouchCancel () {
        clearTimeout(this._touchTimer)
        this._touchTimer = null
        this._touchDragActive = false
        this._touchDragIndex = null
        this.set({ touchHeld: null, overIndex: null })
      },

      // Keyboard tab reordering (accessibility): with a nav tab focused, Alt+Shift+ArrowLeft/Right
      // moves it one position — drag/touch alone aren't usable with a screen reader. Each move is
      // announced via the aria-live region ("<label> — position N of M") so the user knows where the
      // tab landed. preventDefault stops the browser's Alt+Arrow back/forward navigation.
      onKeydown (event, index) {
        if (!event.altKey || !event.shiftKey) {
          return
        }
        const { key } = event
        let toIndex
        if (key === 'ArrowLeft') {
          toIndex = index - 1
        } else if (key === 'ArrowRight') {
          toIndex = index + 1
        } else {
          return
        }
        event.preventDefault()
        event.stopPropagation()
        const { navPages } = store.get()
        const total = navPages.length
        const moved = navPages[index]
        if (!moved) {
          return
        }
        if (toIndex < 0 || toIndex >= total) {
          // already at an edge — re-announce the unchanged position so the keypress still gives feedback
          announceAriaLivePolite(formatIntl('intl.tabReordered', { label: moved.label, position: index + 1, total }))
          return
        }
        this._reorderTabs(index, toIndex)
        announceAriaLivePolite(formatIntl('intl.tabReordered', { label: moved.label, position: toIndex + 1, total }))
        // Keep focus on the moved tab at its new position. The keyed {#each} reuses the node, but
        // refocus defensively after the re-render; preventScroll avoids a jump on the nav bar.
        const href = moved.href
        requestAnimationFrame(() => {
          const nav = document.getElementById('main-nav')
          const link = nav && nav.querySelector(`a[href="${href}"]`)
          if (link) {
            link.focus({ preventScroll: true })
          }
        })
      },

      _reorderTabs (fromIndex, toIndex) {
        const { navPages, currentInstance, navTabOrder } = store.get()
        const newOrder = navPages.map(p => p.name)
        const [moved] = newOrder.splice(fromIndex, 1)
        newOrder.splice(toIndex, 0, moved)
        store.set({
          navTabOrder: Object.assign({}, navTabOrder, { [currentInstance]: newOrder })
        })
      }
    }
  }
</script>
